import { useEffect, useMemo, useRef, useState, Key } from 'react'
import { useDispatch } from 'react-redux'
import { Form } from 'antd'
import axios, { CancelTokenSource } from 'axios'
import { Typography } from '../../../../../components/Typography'
import { Popup } from '../../../../../components/Popup'
import { Table } from '../../../../../components/Table'
import { Input } from '../../../../../components/Input'
import { FieldWithLabel } from '../../../../../components/FieldWithLabel/FieldWithLabel'
import { notification } from '../../../../../components/Notification'
import { Button, BUTTON_TYPES } from '../../../../../components/Button'
import { BTN_TXT, PLACEHOLDERS } from '../../../../../constants'
import { VALIDATION_MESSAGES } from '../../../../../constants'
import { ReactComponent as Plus16 } from 'assets/svg/Plus16.svg'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import { ReactComponent as Close16 } from 'assets/svg/Clos16.svg'
import { useContractDetailsContext } from '../../../Providers/ContractDetailsContextProvider'
import { useContractDetailsPopup } from '../../../Providers/ContractDetailsPopupProvider'
import { useDebounce } from '../../../../../hooks/hooks'
import { isTableHasParams } from '../../../../../helper/common'
import { getCCTableData } from './api'
import { setLoading } from '../../../../../redux/store/common/slice'
import { DEFAULT_PAGE } from '../../../../../components/Table/constants'
import { columns } from './constants'
import {
  AddContractCategoryParams,
  AddContractCategoryProps,
  AddContractCategoryRequestData,
  AddContractCategoryTableData
} from './types'
import './styles.scss'
import { NoCategoriesWarning } from './NoCategoriesWarning'
import { routes } from '../../../../../router'
import { Link } from 'react-router-dom'

export const AddContractCategory = ({
  form,
  isCreation
}: AddContractCategoryProps) => {
  const [firstLoad, setFirstLoad] = useState(true)
  const [searchValue, setSearchValue] = useState('')
  const [totalNumber, setTotalNumber] = useState(0)
  const [initTotalNumber, setInitTotalNumber] = useState<number>(0)
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [tableData, setTableData] = useState<AddContractCategoryTableData[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState<
    AddContractCategoryTableData[]
  >([])
  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([])
  const [sortParams, setSortParams] = useState<AddContractCategoryRequestData>()

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const tableDataTokenRef = useRef<CancelTokenSource>()

  const selectedCCId = Form.useWatch('contract_category', form)
  const organization_contract_category = Form.useWatch(
    'organization_contract_category',
    form
  )

  const {
    addContractCategoryPopup: {
      state: { visible },
      actions: { open, close }
    }
  } = useContractDetailsPopup()

  const {
    state: { details },
    actions: { setLedBy }
  } = useContractDetailsContext()

  const selectedCC = useMemo(
    () =>
      details.contract_category_id && details.contract_category_name
        ? {
            uuid: details.contract_category_id,
            name: details.contract_category_name
          }
        : tableData.find((row) => row.uuid === selectedCCId),
    [
      selectedCCId,
      tableData,
      details.contract_category_id,
      details.contract_category_name
    ]
  )

  const dispatch = useDispatch()

  const debouncedSearch = useDebounce(searchValue, 500)

  useEffect(() => {
    return () => {
      if (tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    if (visible) {
      timeoutRef.current = setTimeout(
        () => {
          void getTableData({ ...sortParams, page: { ...DEFAULT_PAGE } })
          setPageInfo({ ...DEFAULT_PAGE })
        },
        firstLoad ? 0 : 500
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, visible])

  const getTableData = async ({
    sortField,
    sortOrder,
    page
  }: AddContractCategoryRequestData) => {
    try {
      dispatch(setLoading(true))
      if (tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
      const params: AddContractCategoryParams = {}
      if (searchValue) {
        params.search = searchValue
      }
      if (sortField) {
        if (sortOrder) {
          params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
        }
        setSortParams({
          sortField,
          sortOrder
        })
      }
      const dataPage = page ?? pageInfo
      params.limit = dataPage.pageSize
      params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
      tableDataTokenRef.current = axios.CancelToken.source()
      const { data } = await getCCTableData({
        cancelToken: tableDataTokenRef.current?.token,
        params
      })
      if (!data?.results) {
        return
      }
      const tableData: AddContractCategoryTableData[] = []
      const expandedKeys: Key[] = []
      for (const row of data.results) {
        expandedKeys.push(row.uuid)
        tableData.push({
          ...row,
          led_by: row.led_by[0].led_by,
          key: row.uuid,
          children: row.led_by.map((i) => ({
            ...i,
            categoryId: row.uuid,
            key: i.uuid,
            name: row.name
          }))
        })
      }
      setExpandedRowKeys(expandedKeys)
      setTableData(tableData)
      setTotalNumber(data.count)
      if (firstLoad) {
        setFirstLoad(false)
        setInitTotalNumber(data.count)
      }
    } finally {
      dispatch(setLoading(false))
    }
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    void getTableData({
      ...sortParams,
      page
    })
    setPageInfo(page)
  }

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    void getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      page
    })
    setPageInfo(page)
  }

  const handleSelectRow = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys)
    setSelectedRows(selectedRows)
  }

  const onCloseModal = () => {
    setSelectedRowKeys([])
    setSelectedRows([])
    close()
  }

  const onCloseUsavedModal = () => {
    onCloseModal()
    setLedBy('')
  }

  const onAddCategories = () => {
    if (!selectedRowKeys.length) {
      notification.error({
        message: VALIDATION_MESSAGES.SM0208
      })
    } else {
      form.setFieldsValue({
        organization_contract_category: selectedRowKeys[0],
        contract_category: selectedRows[0].categoryId,
        vendor_fee_type: undefined
      })
      setLedBy(selectedRows[0].led_by)
      onCloseModal()
    }
  }

  const handleEdit = () => {
    const categoryChildren = tableData.find(
      (row) => row.uuid === selectedCCId
    )?.children
    const category =
      categoryChildren?.find(
        (row) => row.uuid === organization_contract_category
      ) ?? {}
    if (category) {
      handleSelectRow([organization_contract_category], [category])
    }
    open()
  }

  return (
    <>
      <FieldWithLabel
        title="Contract category"
        className="add-contract-category__label"
      >
        {selectedCC?.uuid ? (
          isCreation ? (
            <Button
              className="add-contract-category__add-btn"
              onClick={handleEdit}
              type={BUTTON_TYPES.GHOST}
              upperCase
              medium
            >
              {selectedCC.name}
            </Button>
          ) : (
            <Link
              to={`${routes.contracts}${routes.contractCategories}/${
                selectedCC.uuid || ''
              }`}
            >
              <div className="mt-8"> {selectedCC.name}</div>
            </Link>
          )
        ) : (
          <Button
            className="add-contract-category__add-btn"
            onClick={open}
            type={BUTTON_TYPES.GHOST}
            icon={<Plus16 />}
            upperCase
            medium
          >
            {BTN_TXT.ADD_CONTRACT_CATEGORY}
          </Button>
        )}
      </FieldWithLabel>
      <Popup
        title={
          <div className="column gap-12">
            Add Contract category
            <Typography.Paragraph>
              Please select one contract category to continue.
            </Typography.Paragraph>
          </div>
        }
        width={910}
        className="add-contract-category"
        visible={visible}
        onCancel={onCloseUsavedModal}
        footer={
          <>
            <Button
              onClick={onCloseUsavedModal}
              type={BUTTON_TYPES.DEFAULT}
              upperCase
            >
              {BTN_TXT.CANCEL}
            </Button>
            <Button
              type={BUTTON_TYPES.PRIMARY}
              onClick={onAddCategories}
              upperCase
            >
              {BTN_TXT.ADD}
            </Button>
          </>
        }
      >
        {!firstLoad && !initTotalNumber && (
          <NoCategoriesWarning className="mb-24" />
        )}
        <div className="add-contract-category__actions row align-center">
          <Input
            className="allow-clear add-contract-category-search"
            propsInput={{
              allowClear: { clearIcon: <Close16 /> },
              placeholder: PLACEHOLDERS.SEARCH,
              prefix: <Search16 />,
              value: searchValue,
              onChange: (e) => setSearchValue(e.target.value)
            }}
          />
        </div>
        <Table<AddContractCategoryTableData>
          dataSource={tableData}
          columns={columns}
          className="add-contract-category__table mt-8"
          scroll={{
            scrollToFirstRowOnChange: true,
            x: 'max-content'
          }}
          onChange={handleTableChange}
          onChangePage={handleChangePageSize}
          pageSize={pageInfo.pageSize}
          pagination={{
            pageSize: pageInfo.pageSize,
            current: pageInfo.pageNumber,
            total: totalNumber
          }}
          expandedRowKeys={expandedRowKeys}
          hasSearchOrFilters={isTableHasParams(debouncedSearch, false)}
          rowSelection={{
            type: 'radio',
            selectedRowKeys,
            onChange: handleSelectRow
          }}
        />
      </Popup>
      <Input
        propsItem={{
          name: 'organization_contract_category',
          hidden: true,
          rules: [
            {
              required: true,
              message: ''
            }
          ]
        }}
      />
      <Input
        propsItem={{
          name: 'contract_category',
          hidden: true,
          rules: [
            {
              required: true,
              message: ''
            }
          ]
        }}
      />
    </>
  )
}
